import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Product } from '../types';
import { useCart } from '../contexts/CartContext';
import { productImage, onImgError } from '../utils/images';
import FavoriteButton from './FavoriteButton';
import { productUrl } from '../services/api';

interface ProductTableProps {
  products: Product[];
}

// Dense table view with multi-select, per-row quantities and bulk add-to-cart.
const ProductTable: React.FC<ProductTableProps> = ({ products }) => {
  const { addToCart } = useCart();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [qty, setQty] = useState<Record<string, number>>({});
  const [added, setAdded] = useState(false);

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === products.length) setSelected(new Set());
    else setSelected(new Set(products.map((p) => String(p.id))));
  };

  const setQuantity = (id: string, value: number) => {
    setQty((prev) => ({ ...prev, [id]: Math.max(1, Math.min(99, value || 1)) }));
  };

  const addSelected = () => {
    products
      .filter((p) => selected.has(String(p.id)))
      .forEach((p) =>
        addToCart({
          id: String(p.id),
          asin: p.asin || '',
          name: p.name,
          image: productImage(p),
          quantity: qty[String(p.id)] || 1,
        })
      );
    setAdded(true);
    setSelected(new Set());
    setTimeout(() => setAdded(false), 2500);
  };

  return (
    <div className="bg-white rounded-xl border border-earth-100 overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-earth-100 bg-cream-50">
        <label className="flex items-center gap-2 text-sm text-earth-700">
          <input
            type="checkbox"
            checked={selected.size === products.length && products.length > 0}
            onChange={toggleAll}
            className="h-4 w-4 accent-sage-600"
          />
          Select all ({products.length})
        </label>
        <button
          type="button"
          onClick={addSelected}
          disabled={selected.size === 0}
          className="btn btn-primary btn-sm disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {added ? 'Added to cart' : `Add ${selected.size > 0 ? selected.size : ''} selected`}
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-earth-500 border-b border-earth-100">
              <th className="px-4 py-2 w-8"></th>
              <th className="px-2 py-2">Product</th>
              <th className="px-2 py-2 hidden md:table-cell">Score</th>
              <th className="px-2 py-2 hidden sm:table-cell">Rating</th>
              <th className="px-2 py-2">Price</th>
              <th className="px-2 py-2 w-20">Qty</th>
              <th className="px-2 py-2 w-10"></th>
            </tr>
          </thead>
          <tbody>
            {products.map((p) => {
              const id = String(p.id);
              return (
                <tr key={id} className={`border-b border-earth-50 ${selected.has(id) ? 'bg-sage-50/60' : ''}`}>
                  <td className="px-4 py-2">
                    <input
                      type="checkbox"
                      checked={selected.has(id)}
                      onChange={() => toggle(id)}
                      aria-label={`Select ${p.name}`}
                      className="h-4 w-4 accent-sage-600"
                    />
                  </td>
                  <td className="px-2 py-2">
                    <Link to={productUrl(p)} className="flex items-center gap-3 group min-w-0">
                      <img
                        src={productImage(p)}
                        alt=""
                        loading="lazy"
                        onError={onImgError()}
                        className="h-10 w-10 rounded-md object-cover bg-cream-200 flex-shrink-0"
                      />
                      <span className="text-earth-800 group-hover:text-sage-700 line-clamp-2 transition-colors">
                        {p.name}
                      </span>
                    </Link>
                  </td>
                  <td className="px-2 py-2 hidden md:table-cell">
                    {p.healthScore > 0 ? (
                      <span className="inline-flex items-center justify-center h-7 w-7 rounded-full bg-sage-600 text-cream-50 text-xs font-bold">
                        {p.healthScore}
                      </span>
                    ) : '·'}
                  </td>
                  <td className="px-2 py-2 hidden sm:table-cell">
                    {p.rating > 0 ? `${p.rating.toFixed(1)} ★` : '·'}
                  </td>
                  <td className="px-2 py-2 font-semibold text-sage-700 whitespace-nowrap">
                    {p.price > 0 ? `$${p.price.toFixed(2)}` : <span className="text-earth-400 font-normal">·</span>}
                  </td>
                  <td className="px-2 py-2">
                    <input
                      type="number"
                      min={1}
                      max={99}
                      value={qty[id] || 1}
                      onChange={(e) => setQuantity(id, parseInt(e.target.value, 10))}
                      aria-label={`Quantity for ${p.name}`}
                      className="w-14 px-2 py-1 border border-earth-200 rounded-md text-center focus:outline-none focus:ring-1 focus:ring-sage-500"
                    />
                  </td>
                  <td className="px-2 py-2">
                    <FavoriteButton productId={p.id} className="!h-8 !w-8" />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ProductTable;
